import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Box,
  Button,
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  Heading,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  SimpleGrid,
  Text,
  useDisclosure,
} from "@chakra-ui/react";
import { useTranslation } from "next-i18next";
import React, { useCallback, useMemo, useRef } from "react";
import { Edit2, Plus, UserPlus, UserX } from "react-feather";
import { Controller, useFormContext } from "react-hook-form";

import Avatar from "@/components/common/Avatar";
import BoxCard from "@/components/common/BoxCard";
import HelpTooltip from "@/components/common/HelpTooltip";
import SelectCreatable from "@/components/common/SelectCreatable";
import StatusIcon from "@/components/common/StatusIcon";
import { useToastPromise } from "@/hooks/useToast";
import { mutateSession, useUser } from "@/hooks/useUser";
import { UserInvitationStatuses } from "@/types/user";
import axios from "axios";
import { mutate } from "swr";

type EditorialOffice = {
  id: number;
  name: string;
  picture: string;
  activated: boolean;
  editorialOfficeUser: boolean;
  officeRequest?: {
    id: number;
    type: "invitation" | "request";
    status: UserInvitationStatuses;
  };
};

type Props = {
  editorialOffices: EditorialOffice[];
  mutateData: () => Promise<any>;
  invitationModalOpen: () => void;
  suggestEditorialOffice: (name: string) => void;
  rounded?: boolean;
};

const BasicInformations = ({
  editorialOffices,
  mutateData,
  invitationModalOpen,
  suggestEditorialOffice,
  rounded = true,
}: Props): React.ReactElement => {
  const { t } = useTranslation("common");
  const [{ user }] = useUser();
  const cancelRef = useRef();
  const toast = useToastPromise();
  const {
    onOpen: invitationAcceptModalOnOpen,
    isOpen: invitationAcceptModalIsOpen,
    onClose: invitationAcceptModalOnClose,
  } = useDisclosure();

  const {
    onOpen: newEditorialModalOnOpen,
    isOpen: newEditorialModalIsOpen,
    onClose: newEditorialModalOnClose,
  } = useDisclosure();

  const options = useMemo(
    () =>
      [
        editorialOffices?.map((office) => ({
          value: office.id,
          plainLabel: office.name,
          inSearchOnly:
            !office.editorialOfficeUser &&
            (!office.officeRequest ||
              (office.officeRequest.type !== "invitation" &&
                office.officeRequest.status !==
                  UserInvitationStatuses.WAITING)),
          label: (
            <HStack spacing={3}>
              {office?.officeRequest?.type === "request" &&
              office?.officeRequest?.status ===
                UserInvitationStatuses.WAITING ? (
                <>
                  <Avatar
                    src={
                      user?.profile?.picture
                        ? `/uploads/${user?.profile?.picture}`
                        : null
                    }
                    name={`${user?.firstName} ${user?.lastName}`}
                    size="xs"
                  />
                  <Text>{t("events.freelancer")}</Text>
                  <Text color="gray.500">({office.name})</Text>
                </>
              ) : (
                <>
                  <Avatar
                    src={office.picture ? `/uploads/${office.picture}` : null}
                    name={office.name}
                    size="xs"
                    opacity={!office.activated ? "50%" : undefined}
                  />
                  <Text
                    color={
                      office?.officeRequest?.status ===
                      UserInvitationStatuses.REJECTED
                        ? "red.500"
                        : !office.activated
                        ? "gray.300"
                        : undefined
                    }
                  >
                    {office.name}
                  </Text>
                </>
              )}

              {!office.activated && (
                <StatusIcon
                  status="editorialOffice.accreditationInformation.pendingStatus"
                  icon={Edit2}
                  color="red.500"
                  _hover={{ color: "red.800" }}
                />
              )}
              {
                /* if user does not belong to the office */ !office.editorialOfficeUser &&
                  (!office.officeRequest ||
                  office.officeRequest.status ===
                    UserInvitationStatuses.ACCEPTED /* when there was no invitation or we have an old one */ ? (
                    <StatusIcon
                      status="editorialOffice.accreditationInformation.notOfficeUser"
                      icon={UserPlus}
                      color="green.500"
                      _hover={{ color: "green.800" }}
                    />
                  ) : office.officeRequest.status ===
                    UserInvitationStatuses.REJECTED /* when user invitation has been rejected by the office */ ? (
                    <StatusIcon
                      status="editorialOffice.accreditationInformation.userHasBeenRejected"
                      icon={UserX}
                      color="red.500"
                      _hover={{ color: "red.800" }}
                    />
                  ) : office.officeRequest.type ===
                    "invitation" /* when you has got an invitation from the office */ ? (
                    <StatusIcon
                      status="editorialOffice.accreditationInformation.userHasGotInvitation"
                      icon={UserPlus}
                      color="green.500"
                      _hover={{ color: "green.800" }}
                    />
                  ) : office.officeRequest.status ===
                    UserInvitationStatuses.WAITING /* when user have still waiting invitation sent to the office */ ? (
                    <StatusIcon
                      status="editorialOffice.accreditationInformation.userSentRequest"
                      icon={UserPlus}
                      color="green.500"
                      _hover={{ color: "green.800" }}
                    />
                  ) : null)
              }
            </HStack>
          ),
          isDisabled:
            office?.officeRequest?.status === UserInvitationStatuses.REJECTED,
        })),
        {
          value: "freelancer",
          plainLabel: "freelancer",
          label: (
            <HStack spacing={3}>
              <Avatar
                src={
                  user?.profile?.picture
                    ? `/uploads/${user?.profile?.picture}`
                    : null
                }
                name={`${user?.firstName} ${user?.lastName}`}
                size="xs"
              />
              <Text>{`${t("events.freelancer")} - ${user?.firstName} ${
                user?.lastName
              }`}</Text>
            </HStack>
          ),
        },
        {
          value: "new",
          plainLabel: "new",
          label: (
            <HStack spacing={3}>
              <Plus />
              <Text>{t("events.addNewEditorial")}</Text>
            </HStack>
          ),
        },
      ].flat(1),
    [editorialOffices]
  );

  const {
    formState: { errors },
    control,
    register,
    watch,
    setValue,
  } = useFormContext();

  const editorialOffice = watch("editorialOffice");

  const editorialOfficeName = useMemo(
    () =>
      editorialOffices?.find((office) => office.id === editorialOffice)?.name,
    [editorialOffice]
  );

  const closeInvitationAcceptModal = useCallback(() => {
    invitationAcceptModalOnClose();
    setValue("editorialOffice", null);
  }, []);

  const onInvitationAccept = useCallback(() => {
    const office = editorialOffices.find(
      (office) => office.id === editorialOffice
    );

    return toast.promise(
      axios
        .put(`/office-request/request/${office.officeRequest.id}`, {
          status: "accept",
        })
        .then(() => {
          invitationAcceptModalOnClose();
          mutateData();
          mutate("/office-request/invitations");
          mutateSession();
        }),
      {
        errorKeys: {
          InvitationDoesNotExists: t("errorKeys.invitationDoesNotExists"),
        },
      }
    );
  }, []);

  const preventPaste = (e) => {
    e.preventDefault();
  };

  const edOffice = watch("editorialOffice");

  const editorialOfficeValue = useMemo(() => {
    if (editorialOffice)
      return options?.find((option) => option?.value == editorialOffice);
    return null;
  }, [editorialOffice, options]);

  return (
    <>
      <BoxCard p={0} mt={3} borderRadius={rounded ? ".375rem" : 0}>
        <Box p="1.25rem" borderBottomColor="light.50" borderBottomWidth="1px">
          <Heading as="h3" size="md">
            {t("events.journalistData")}
          </Heading>
        </Box>
        <Box p="1.25rem">
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
            <FormControl isRequired isInvalid={!!errors?.editorialOffice}>
              <FormLabel
                pb={1.5}
                verticalAlign="middle"
                d="inline-flex"
                alignItems="center"
              >
                {t("events.myEditorialOffice")}
                <HelpTooltip label="accreditation.myEditorialOffice" />
              </FormLabel>
              <Controller
                control={control}
                name="editorialOffice"
                rules={{
                  required: true,
                  validate: (value) => {
                    const office = editorialOffices?.find(
                      (office) => office.id === value
                    );

                    return !value ||
                      office?.officeRequest?.status !==
                        UserInvitationStatuses.REJECTED
                      ? true
                      : `${t(
                          "editorialOffice.accreditationInformation.userHasBeenRejected"
                        )}`;
                  },
                }}
                render={({ field: { onChange, value, ref } }) => (
                  <SelectCreatable
                    rounded={rounded}
                    options={options}
                    defaultValue={options?.find(
                      (option) => option?.value == editorialOffice
                    )}
                    ref={ref}
                    onChange={(selectedValue) => {
                      if (selectedValue?.value != "new") {
                        onChange(selectedValue?.value);

                        const office = editorialOffices.find(
                          (office) => office.id === selectedValue.value
                        );

                        if (office && !office.editorialOfficeUser) {
                          if (
                            !office.officeRequest ||
                            office.officeRequest.status ===
                              UserInvitationStatuses.ACCEPTED
                          ) {
                            if (invitationModalOpen) invitationModalOpen();
                          } else if (
                            office.officeRequest?.type === "invitation" &&
                            office.officeRequest?.status ===
                              UserInvitationStatuses.WAITING
                          ) {
                            invitationAcceptModalOnOpen();
                          }
                        }
                      } else {
                        newEditorialModalOnOpen();
                      }
                    }}
                    value={editorialOfficeValue}
                    isInvalid={!!errors?.editorialOffice}
                    onCreateOption={(value: string) =>
                      suggestEditorialOffice(value)
                    }
                    onBlur={(event) => {
                      if (
                        event.target.value.length > 0 &&
                        !options?.find((c) => event.target.value === c?.value)
                      ) {
                        suggestEditorialOffice(event.target.value);
                      }
                    }}
                    filterOption={(candidate, input) =>
                      candidate.data.__isNew__ ||
                      (candidate.data.plainLabel
                        ?.toLowerCase()
                        ?.includes(input?.toLowerCase()) &&
                        (!candidate.data.inSearchOnly || !!input))
                    }
                    isValidNewOption={(input, _value, options) => {
                      const matchingOptions = options.filter(
                        (option) =>
                          option?.plainLabel
                            ?.toLowerCase()
                            ?.includes(input?.toLowerCase()) &&
                          (!option.inSearchOnly || !!input)
                      );

                      return matchingOptions.length === 0;
                    }}
                    formatCreateLabel={(value) => (
                      <>
                        {t(
                          "editorialOffice.accreditationInformation.createOptionLabel",
                          {
                            name: `"${value}"`,
                          }
                        )}
                      </>
                    )}
                  />
                )}
              />
              {errors.editorialOffice && (
                <FormErrorMessage>
                  {errors.editorialOffice.type === "required"
                    ? t("users.fieldRequired")
                    : errors.editorialOffice.message}
                </FormErrorMessage>
              )}
            </FormControl>
            <FormControl isRequired isDisabled>
              <FormLabel>{t("users.email")}</FormLabel>
              <Input
                {...register("email", {
                  required: {
                    message: t("users.fieldRequired"),
                    value: true,
                  },
                })}
              />
              {errors.email && (
                <FormErrorMessage>{errors?.email?.message}</FormErrorMessage>
              )}
            </FormControl>
            <FormControl isRequired isInvalid={!!errors?.firstName}>
              <FormLabel>{t("users.firstName")}</FormLabel>
              <Input
                onPaste={preventPaste}
                {...register("firstName", {
                  required: {
                    message: t("users.fieldRequired"),
                    value: true,
                  },
                  minLength: {
                    value: 2,
                    message: t("events.fieldMinimumCharsValueInfo", {
                      value: 2,
                    }),
                  },
                  validate: (value) =>
                    /([\-\'a-zA-ZąĄćĆęĘłŁńŃóÓśŚżŻźŹ])+/.test(value),
                })}
              />
              {errors.firstName && (
                <FormErrorMessage>
                  {errors?.firstName?.message}
                </FormErrorMessage>
              )}
            </FormControl>
            <FormControl isRequired isInvalid={!!errors?.lastName}>
              <FormLabel>{t("users.lastName")}</FormLabel>
              <Input
                onPaste={preventPaste}
                {...register("lastName", {
                  required: {
                    message: t("users.fieldRequired"),
                    value: true,
                  },
                  minLength: {
                    value: 2,
                    message: t("events.fieldMinimumCharsValueInfo", {
                      value: 2,
                    }),
                  },
                  validate: (value) =>
                    /([\-\'a-zA-ZąĄćĆęĘłŁńŃóÓśŚżŻźŹ])+/.test(value),
                })}
              />
              {errors.lastName && (
                <FormErrorMessage>{errors?.lastName?.message}</FormErrorMessage>
              )}
            </FormControl>
          </SimpleGrid>
        </Box>
      </BoxCard>

      <AlertDialog
        isOpen={invitationAcceptModalIsOpen}
        leastDestructiveRef={cancelRef}
        onClose={closeInvitationAcceptModal}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              {t(
                "editorialOffice.accreditationInformation.invitationAcceptTitle"
              )}
            </AlertDialogHeader>

            <AlertDialogBody>
              {t("editorialOffice.accreditationInformation.invitationAccept", {
                name: editorialOfficeName,
              })}
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button
                variant="ghost"
                onClick={closeInvitationAcceptModal}
                ref={cancelRef}
              >
                {t("cancel")}
              </Button>
              <Button colorScheme="green" onClick={onInvitationAccept} ml={3}>
                {t("accept")}
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
      {newEditorialModalIsOpen && (
        <Modal
          size="xl"
          motionPreset="slideInBottom"
          isOpen={newEditorialModalIsOpen}
          onClose={newEditorialModalOnClose}
          preserveScrollBarGap
        >
          <ModalOverlay>
            <ModalContent>
              <ModalHeader>{t("events.addNewEditorial")}</ModalHeader>
              <ModalCloseButton />
              <ModalBody p="20px">
                <Text mb="20px">
                  {t("help.accreditation.myEditorialOffice")}
                </Text>
                <FormControl isRequired isInvalid={!!errors?.editorialOffice}>
                  <FormLabel
                    pb={1.5}
                    verticalAlign="middle"
                    d="inline-flex"
                    alignItems="center"
                  >
                    {t("events.myEditorialOffice")}
                  </FormLabel>
                  <Controller
                    control={control}
                    name="editorialOffice"
                    rules={{
                      required: true,
                      validate: (value) => {
                        const office = editorialOffices?.find(
                          (office) => office.id === value
                        );

                        return !value ||
                          office?.officeRequest?.status !==
                            UserInvitationStatuses.REJECTED
                          ? true
                          : `${t(
                              "editorialOffice.accreditationInformation.userHasBeenRejected"
                            )}`;
                      },
                    }}
                    render={({ field: { onChange, value, ref } }) => (
                      <SelectCreatable
                        rounded={rounded}
                        options={options?.slice(0, options.length - 1)}
                        ref={ref}
                        onChange={(selectedValue) => {
                          onChange(selectedValue?.value);

                          const office = editorialOffices.find(
                            (office) => office.id === selectedValue.value
                          );

                          if (office && !office.editorialOfficeUser) {
                            if (
                              !office.officeRequest ||
                              office.officeRequest.status ===
                                UserInvitationStatuses.ACCEPTED
                            ) {
                              if (invitationModalOpen) invitationModalOpen();
                            } else if (
                              office.officeRequest?.type === "invitation" &&
                              office.officeRequest?.status ===
                                UserInvitationStatuses.WAITING
                            ) {
                              invitationAcceptModalOnOpen();
                            }
                          }
                          newEditorialModalOnClose();
                        }}
                        value={
                          value
                            ? options?.find((c) => value === c?.value)
                            : value
                        }
                        isInvalid={!!errors?.editorialOffice}
                        onCreateOption={(value: string) =>
                          suggestEditorialOffice(value)
                        }
                        onBlur={(event) => {
                          if (
                            event.target.value.length > 0 &&
                            !options?.find(
                              (c) => event.target.value === c?.value
                            )
                          ) {
                            suggestEditorialOffice(event.target.value);
                          }
                        }}
                        filterOption={(candidate, input) =>
                          candidate.data.__isNew__ ||
                          (candidate.data.plainLabel
                            ?.toLowerCase()
                            ?.includes(input?.toLowerCase()) &&
                            (!candidate.data.inSearchOnly || !!input))
                        }
                        isValidNewOption={(input, _value, options) => {
                          const matchingOptions = options.filter(
                            (option) =>
                              option?.plainLabel
                                ?.toLowerCase()
                                ?.includes(input?.toLowerCase()) &&
                              (!option.inSearchOnly || !!input)
                          );

                          return matchingOptions.length === 0;
                        }}
                        formatCreateLabel={(value) => (
                          <>
                            {t(
                              "editorialOffice.accreditationInformation.createOptionLabel",
                              {
                                name: `"${value}"`,
                              }
                            )}
                          </>
                        )}
                      />
                    )}
                  />
                  {errors.editorialOffice && (
                    <FormErrorMessage>
                      {errors.editorialOffice.type === "required"
                        ? t("users.fieldRequired")
                        : errors.editorialOffice.message}
                    </FormErrorMessage>
                  )}
                </FormControl>
              </ModalBody>
            </ModalContent>
          </ModalOverlay>
        </Modal>
      )}
    </>
  );
};

export default BasicInformations;
